import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import {
  getRecoveryParamsFromUrl,
  isRecoveryOrInvite,
  redirectToSetPasswordPage,
} from "@/utils/recoveryUrl";
import { canAccess } from "../commons/canAccess";
import { getUserRole } from "../../sales/userRoles";
import { evaluateMfaAuthState } from "../../mfa/evaluateMfaAuthState";
import { isMfaPath } from "../../mfa/constants";
import {
  needsPasswordMfaStepUp,
  shouldReuseSessionForPasswordUpdate,
} from "../../mfa/mfaAuthGuard";
import {
  consumePendingMfaRedirect,
  markPendingMfaRedirect,
} from "../../mfa/mfaRedirectState";
import { getAppRoutePath } from "../../mfa/routePath";
import { getSupabaseClient } from "./supabase";
import {
  fetchAndHydrateUiPreferences,
  resetPreferencesHydration,
} from "../../settings/userPreferences";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale == null) {
        throw new Error();
      }

      // Restore DB-backed language prefs once per session (survives deploys / cross-device).
      void fetchAndHydrateUiPreferences(String(sale.id));

      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
        administrator: sale.administrator,
        manager: sale.manager ?? false,
        onboarding_completed_at: sale.onboarding_completed_at ?? null,
        notifications_enabled: sale.notifications_enabled ?? true,
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

function isAuthExemptPath(pathname: string): boolean {
  return (
    pathname === "/login" ||
    pathname.includes("/login") ||
    pathname === "/set-password" ||
    pathname.includes("/set-password") ||
    pathname === "/forgot-password" ||
    pathname.includes("/forgot-password") ||
    pathname === "/sign-up" ||
    pathname.includes("/sign-up") ||
    isMfaPath(pathname)
  );
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

type CachedSale = {
  id: number;
  first_name: string;
  last_name: string;
  avatar?: { src?: string } | null;
  administrator: boolean;
  manager?: boolean;
  locale?: string | null;
  voice_language?: string | null;
  bc_environment?: string | null;
  notifications_enabled?: boolean | null;
  onboarding_completed_at?: string | null;
};

const getSale = async (): Promise<CachedSale | undefined> => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    try {
      const parsed = JSON.parse(cachedValue) as CachedSale;
      // Pref columns are new — drop stale caches that predate them.
      if (
        !("locale" in parsed) ||
        !("voice_language" in parsed) ||
        !("bc_environment" in parsed) ||
        !("notifications_enabled" in parsed) ||
        !("onboarding_completed_at" in parsed)
      ) {
        storage?.removeItem(CURRENT_SALE_CACHE_KEY);
      } else {
        return parsed;
      }
    } catch {
      storage?.removeItem(CURRENT_SALE_CACHE_KEY);
    }
  }

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select(
      "id, first_name, last_name, avatar, administrator, manager, locale, voice_language, bc_environment, notifications_enabled, onboarding_completed_at",
    )
    .match({ user_id: dataSession?.session?.user.id })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
  return dataSale as CachedSale;
};

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
  resetPreferencesHydration();
}

async function runMfaGuard(pathname: string) {
  const mfaRedirect = await evaluateMfaAuthState(getSupabaseClient(), pathname);
  if (mfaRedirect) {
    markPendingMfaRedirect(mfaRedirect.redirectTo);
    throw {
      redirectTo: mfaRedirect.redirectTo,
      message: false,
    };
  }
}

async function getMfaRedirectAfterLogin() {
  const mfaRedirect = await evaluateMfaAuthState(
    getSupabaseClient(),
    getAppRoutePath(),
  );
  return mfaRedirect?.redirectTo ?? null;
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    setPassword: async (params: {
      access_token: string;
      refresh_token: string;
      password: string;
    }) => {
      const supabase = getSupabaseClient();

      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();
      const [{ data: aalBefore }, { data: factorsBefore }] = await Promise.all([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ]);
      const factorsBeforeNormalized = {
        totp: factorsBefore?.totp ?? [],
        phone: factorsBefore?.phone ?? [],
      };

      // MFA step-up on set-password upgrades the live client session to AAL2.
      // Recovery tokens in the URL remain AAL1 — re-applying them here would
      // downgrade the session and make password updates impossible.
      const reuseCurrentSession = shouldReuseSessionForPasswordUpdate({
        hasCurrentSession: !!currentSession,
        currentLevel: aalBefore?.currentLevel ?? null,
        factors: factorsBeforeNormalized,
      });

      if (!reuseCurrentSession) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: params.access_token,
          refresh_token: params.refresh_token,
        });
        if (sessionError) {
          throw sessionError;
        }
      }

      const [{ data: aalData }, { data: factorsData }] = await Promise.all([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ]);

      const factors = {
        totp: factorsData?.totp ?? [],
        phone: factorsData?.phone ?? [],
      };

      if (
        needsPasswordMfaStepUp({
          currentLevel: aalData?.currentLevel ?? null,
          factors,
        })
      ) {
        throw new Error(
          "AAL2 session is required to update email or password when MFA is enabled.",
        );
      }

      const { error } = await supabase.auth.updateUser({
        password: params.password,
      });
      if (error) {
        throw error;
      }
    },
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      const result = await baseAuthProvider.login(params);
      const mfaRedirect = await getMfaRedirectAfterLogin();
      if (mfaRedirect) {
        return { redirectTo: mfaRedirect };
      }
      return result;
    },
    logout: async (params) => {
      const mfaRedirect = consumePendingMfaRedirect();
      if (mfaRedirect) {
        return mfaRedirect;
      }

      // Keep trusted-device tokens across logout so "remember for 30 days" works.
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      const pathname = getAppRoutePath();

      // Hash-router fallback for auth-exempt pages
      if (
        isAuthExemptPath(pathname) ||
        window.location.hash.includes("#/set-password") ||
        window.location.hash.includes("#/forgot-password") ||
        window.location.hash.includes("#/sign-up") ||
        window.location.hash.includes("#/mfa/")
      ) {
        if (isMfaPath(pathname) || window.location.hash.includes("#/mfa/")) {
          return baseAuthProvider.checkAuth(params);
        }
        return;
      }

      const { access_token, refresh_token, type } = getRecoveryParamsFromUrl();
      if (isRecoveryOrInvite(type) && access_token && refresh_token) {
        // Avoid react-admin logout-on-redirect; navigate directly.
        redirectToSetPasswordPage(access_token, refresh_token, type);
        await new Promise(() => {});
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        await getSupabaseClient().auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      await baseAuthProvider.checkAuth(params);
      await runMfaGuard(pathname);
    },
    handleCallback: async (params) => {
      const result = await baseAuthProvider.handleCallback!(params);
      const pathname = getAppRoutePath();
      if (!isAuthExemptPath(pathname)) {
        await runMfaGuard(pathname);
      }
      return result;
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (!isInitialized) return false;

      // Get the current user
      const sale = await getSale();
      if (sale == null) return false;

      // Compute access rights from the sale role
      const role = getUserRole(sale);
      return canAccess(role, params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
